import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { ArrowLeft, Send, ShieldAlert, Flag, Ban, MoreVertical, ShoppingBag, Package, Info, MessageCircle, Check, CheckCheck, ShieldCheck, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price, currency = 'IDR') => {
  if (currency === 'IDR') return `Rp ${Number(price).toLocaleString('id-ID')}`;
  return `${currency} ${Number(price).toLocaleString()}`;
};

const QUICK_PROMPTS = [
  "Is this still available?",
  "Is the price negotiable?",
  "What's the condition like?",
  "Can I see more photos?",
  "Where are you located?",
];

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
}

function formatDateHeader(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

function MessageBubble({ msg, isOwn }) {
  if (msg.is_system) {
    return (
      <div className="flex justify-center my-3" data-testid={`system-msg-${msg.message_id}`}>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2.5 max-w-[85%]">
          <div className="flex items-start gap-2">
            <ShieldAlert size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-body text-amber-700 leading-relaxed">{msg.text}</p>
          </div>
        </div>
      </div>
    );
  }

  if (msg.blocked) {
    return (
      <div className="flex flex-col items-end mb-2" data-testid={`msg-blocked-${msg.message_id}`}>
        <div className="max-w-[75%] px-4 py-2.5 bg-red-50 border border-red-200 text-red-400 rounded-2xl rounded-br-md opacity-70">
          <p className="text-sm font-body leading-relaxed whitespace-pre-wrap break-words line-through">{msg.text}</p>
          <p className="text-xs font-body text-red-400 mt-1">Your message was not sent</p>
        </div>
        <div className="max-w-[75%] mt-1.5 bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2">
          <div className="flex items-start gap-1.5">
            <ShieldAlert size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-body text-amber-700 leading-relaxed">{msg.block_reason}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`msg-${msg.message_id}`}>
      <div className={`max-w-[75%] px-4 py-2.5 ${
        isOwn
          ? 'bg-[#2C2A29] text-white rounded-2xl rounded-br-md'
          : 'bg-white border border-cream-100 text-[#2C2A29] rounded-2xl rounded-bl-md'
      }`}>
        <p className="text-sm font-body leading-relaxed whitespace-pre-wrap break-words">{msg.text}</p>
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-[10px] font-body ${isOwn ? 'text-white/50' : 'text-[#A89F91]'}`}>
            {formatTime(msg.created_at)}
          </span>
          {isOwn && msg.status === 'read' && (
            <CheckCheck size={13} className="text-blue-400" data-testid={`msg-read-${msg.message_id}`} />
          )}
          {isOwn && msg.status === 'sent' && (
            <Check size={13} className="text-white/50" data-testid={`msg-sent-${msg.message_id}`} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations/${conversationId}`);
      setConversation(res.data);
      setMessages(res.data.messages || []);
    } catch {
      toast.error('Conversation not found');
      navigate('/messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchConversation();
  }, [isAuthenticated, fetchConversation]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Poll every 5s for new messages + instant refetch on tab visible / window focus
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchNow = async () => {
      try {
        const res = await axios.get(`${API}/messages/conversations/${conversationId}`);
        // Preserve locally-only "blocked" messages (not persisted on backend) across polls.
        setMessages(prev => {
          const localBlocked = prev.filter(m => m.blocked);
          return [...(res.data.messages || []), ...localBlocked];
        });
        setConversation(prev => ({ ...prev, is_blocked: res.data.is_blocked, blocked_other: res.data.blocked_other, is_closed: res.data.is_closed, closed_reason: res.data.closed_reason }));
      } catch {}
    };
    const interval = setInterval(fetchNow, 5000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchNow(); };
    const onFocus = () => fetchNow();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAuthenticated, conversationId]);

  const handleSend = async (text) => {
    const msgText = (text || newMsg).trim();
    if (!msgText || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages/conversations/${conversationId}/send`, { text: msgText });
      if (res.data.blocked) {
        // Message was blocked — show it locally as unsent
        const blockedMsg = {
          message_id: `blocked_${Date.now()}`,
          sender_id: user?.user_id,
          text: msgText,
          created_at: new Date().toISOString(),
          is_system: false,
          blocked: true,
          block_reason: res.data.reason,
        };
        setMessages(prev => [...prev, blockedMsg]);
        setNewMsg('');
      } else {
        setMessages(prev => [...prev, res.data.message]);
        setNewMsg('');
      }
      if (inputRef.current) inputRef.current.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason) { toast.error('Please select a reason'); return; }
    setReporting(true);
    try {
      await axios.post(`${API}/messages/conversations/${conversationId}/report`, { reason: reportReason });
      toast.success('Conversation reported. Our team will review it.');
      setReportDialog(false);
      setReportReason('');
      setMenuOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to report');
    } finally {
      setReporting(false);
    }
  };

  const handleBlock = async () => {
    try {
      const res = await axios.post(`${API}/messages/conversations/${conversationId}/block`);
      toast.success(res.data.message);
      setConversation(prev => ({ ...prev, blocked_other: res.data.blocked }));
      setMenuOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-cream-100 rounded w-32" />
            <div className="h-20 bg-cream-100 rounded-2xl" />
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-cream-100 rounded-2xl" />)}</div>
          </div>
        </div>
      </div>
    );
  }

  const ctx = conversation?.context || {};
  const isListingChat = conversation?.type === 'listing';
  const isOrderChat = conversation?.type === 'order';
  const isAdminDm = conversation?.type === 'admin_dm' || conversation?.is_admin_dm;
  const viewerIsAdmin = conversation?.viewer_is_admin;
  const isAdminChannelForUser = conversation?.other_user?.is_admin_channel;
  const isBlocked = conversation?.is_blocked || conversation?.blocked_other;
  const isClosed = conversation?.is_closed && !isBlocked;
  const closedReason = conversation?.closed_reason;

  const handleToggleAdminDm = async () => {
    if (!conversation?.conversation_id) return;
    const endpoint = conversation.is_closed ? 'reopen' : 'close';
    try {
      await axios.post(`${API}/admin/messages/conversations/${conversation.conversation_id}/${endpoint}`);
      toast.success(conversation.is_closed ? 'Conversation reopened' : 'Conversation closed');
      const { data } = await axios.get(`${API}/messages/conversations/${conversation.conversation_id}`);
      setConversation(prev => ({ ...prev, ...data }));
      setMenuOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Group messages by date
  const messagesByDate = {};
  messages.forEach(msg => {
    const dateKey = new Date(msg.created_at).toDateString();
    if (!messagesByDate[dateKey]) messagesByDate[dateKey] = [];
    messagesByDate[dateKey].push(msg);
  });

  return (
    <div className="min-h-screen bg-[#FCFAF8] flex flex-col">
      <Header />

      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full">
        {/* Chat Header */}
        <div className="bg-white border-b border-cream-100/50 px-4 py-3 sticky top-16 md:top-20 z-40" data-testid="chat-header">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/messages')} className="text-[#78716C] hover:text-[#2C2A29] transition-colors flex-shrink-0" data-testid="chat-back">
              <ArrowLeft size={20} />
            </button>

            {/* Product / Admin DM context */}
            <div
              className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
              onClick={() => {
                if (isListingChat && ctx.listing_id) navigate(`/listing/${ctx.listing_id}`);
              }}
            >
              <div className={`w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 ${isAdminDm ? 'bg-purple-100' : 'bg-cream-50'}`}>
                {isAdminDm ? (
                  <ShieldCheck size={18} className="text-purple-600" />
                ) : ctx.photo ? (
                  <img src={ctx.photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  isOrderChat ? <Package size={16} className="text-[#A89F91]" /> : <ShoppingBag size={16} className="text-[#A89F91]" />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-body text-sm font-semibold text-[#2C2A29] truncate flex items-center gap-1">
                  {conversation?.other_user?.name || 'Unknown'}
                  {isAdminChannelForUser && <ShieldCheck size={13} className="text-purple-600 flex-shrink-0" />}
                </p>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-body font-bold uppercase tracking-wider ${
                    isAdminDm ? 'bg-purple-100 text-purple-700' :
                    isListingChat ? 'bg-[#FFF5F5] text-[#D48585]' : 'bg-blue-50 text-blue-600'
                  }`}>
                    {isAdminDm ? (isAdminChannelForUser ? 'Official' : 'Admin DM') : isListingChat ? 'Listing' : 'Order'}
                  </span>
                  <p className="text-xs font-body text-[#78716C] truncate">
                    {isAdminDm ? (viewerIsAdmin ? 'Direct message' : 'Stashmart support') : (ctx.product_name || (isOrderChat ? `#${ctx.order_id || ''}` : ''))}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu */}
            <div className="relative flex-shrink-0">
              <button onClick={() => setMenuOpen(!menuOpen)} className="text-[#78716C] hover:text-[#2C2A29] p-1.5 rounded-lg hover:bg-cream-50 transition-all" data-testid="chat-menu-btn">
                <MoreVertical size={18} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-cream-100 shadow-lg z-50 w-48 py-1" data-testid="chat-menu-dropdown">
                    <button
                      onClick={() => { setMenuOpen(false); setReportDialog(true); }}
                      className="w-full text-left px-4 py-2.5 text-sm font-body text-[#78716C] hover:bg-cream-50 hover:text-red-500 flex items-center gap-2 transition-colors"
                      data-testid="chat-report-btn"
                    >
                      <Flag size={14} /> Report conversation
                    </button>
                    <button
                      onClick={handleBlock}
                      className="w-full text-left px-4 py-2.5 text-sm font-body text-[#78716C] hover:bg-cream-50 hover:text-red-500 flex items-center gap-2 transition-colors"
                      data-testid="chat-block-btn"
                    >
                      <Ban size={14} /> {conversation?.blocked_other ? 'Unblock user' : 'Block user'}
                    </button>
                    {isAdminDm && viewerIsAdmin && (
                      <button
                        onClick={handleToggleAdminDm}
                        className="w-full text-left px-4 py-2.5 text-sm font-body text-[#78716C] hover:bg-cream-50 hover:text-purple-700 flex items-center gap-2 transition-colors border-t border-cream-100/50"
                        data-testid="admin-dm-toggle-btn"
                      >
                        {conversation?.is_closed ? <><Unlock size={14} /> Reopen conversation</> : <><Lock size={14} /> Close conversation</>}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Context card */}
          {ctx.price && isListingChat && (
            <div className="mt-2 bg-cream-50 rounded-xl px-3 py-2 flex items-center justify-between" data-testid="chat-listing-context">
              <span className="text-xs font-body text-[#78716C]">{ctx.brand}</span>
              <span className="text-xs font-body font-semibold text-[#2C2A29]">{formatPrice(ctx.price, ctx.currency)}</span>
            </div>
          )}
          {isOrderChat && ctx.payment_status && (
            <div className="mt-2 bg-blue-50 rounded-xl px-3 py-2 flex items-center justify-between" data-testid="chat-order-context">
              <span className="text-xs font-body text-blue-600">Order #{ctx.order_id}</span>
              <span className="text-xs font-body font-semibold text-blue-800 capitalize">{ctx.payment_status?.replace(/_/g, ' ')}</span>
            </div>
          )}
        </div>

        {/* Safety Notice */}
        <div className="px-4 py-2" data-testid="safety-notice">
          <div className="bg-[#FFF5F5] rounded-xl px-3 py-2 flex items-center gap-2">
            <Info size={12} className="text-[#D48585] flex-shrink-0" />
            <p className="text-xs font-body text-[#D48585]">For your safety, keep payment and communication on Stashmart.</p>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="messages-area">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <MessageCircle size={32} className="mx-auto text-cream-200 mb-2" />
              <p className="text-sm font-body text-[#A89F91]">No messages yet. Start the conversation!</p>
            </div>
          )}

          {Object.entries(messagesByDate).map(([dateKey, dateMsgs]) => (
            <div key={dateKey}>
              <div className="flex items-center justify-center my-4">
                <span className="text-[11px] font-body text-[#A89F91] bg-cream-50 px-3 py-1 rounded-full">{formatDateHeader(dateMsgs[0]?.created_at)}</span>
              </div>
              {dateMsgs.map(msg => (
                <MessageBubble key={msg.message_id} msg={msg} isOwn={msg.sender_id === user?.user_id} />
              ))}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick prompts (listing chat, no messages yet from current user) */}
        {isListingChat && messages.filter(m => m.sender_id === user?.user_id).length === 0 && !isBlocked && !isClosed && (
          <div className="px-4 pb-2" data-testid="quick-prompts">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
              {QUICK_PROMPTS.map(prompt => (
                <button
                  key={prompt}
                  onClick={() => handleSend(prompt)}
                  disabled={sending}
                  className="flex-shrink-0 bg-white border border-cream-100 rounded-full px-3 py-1.5 text-xs font-body text-[#78716C] hover:border-[#F4C2C2] hover:text-[#2C2A29] hover:bg-[#FFF5F5] transition-all disabled:opacity-50"
                  data-testid={`quick-prompt-${prompt.replace(/\s+/g, '-').toLowerCase().replace(/[?']/g, '')}`}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="border-t border-cream-100/50 bg-white px-4 py-3 sticky bottom-0" data-testid="chat-input-area">
          {isBlocked ? (
            <div className="text-center py-2">
              <p className="text-sm font-body text-[#A89F91]">
                {conversation?.is_blocked ? 'You have been blocked in this conversation.' : 'You have blocked this user.'}
              </p>
              {conversation?.blocked_other && (
                <button onClick={handleBlock} className="text-xs font-body text-[#D48585] underline mt-1" data-testid="unblock-btn">Unblock</button>
              )}
            </div>
          ) : isClosed ? (
            <div className="text-center py-2" data-testid="chat-closed-notice">
              <p className="text-sm font-body text-[#A89F91]">
                {closedReason === 'completed' && 'This transaction is complete. The chat is now closed.'}
                {closedReason === 'cancelled' && 'This order was cancelled. The chat is now closed.'}
                {closedReason === 'refunded' && 'This order was refunded. The chat is now closed.'}
                {closedReason === 'failed' && 'This order did not go through. The chat is now closed.'}
                {closedReason === 'return_reported' && 'The buyer reported a problem. Admin is reviewing — the chat is temporarily closed.'}
                {closedReason === 'admin_closed' && (viewerIsAdmin ? 'You closed this conversation. Reopen from the menu to resume.' : 'Stashmart admin has closed this conversation.')}
                {!['completed', 'cancelled', 'refunded', 'failed', 'return_reported', 'admin_closed'].includes(closedReason) && 'This chat is closed.'}
              </p>
              <p className="text-xs font-body text-[#A89F91] mt-1">
                {closedReason === 'return_reported'
                  ? 'If the buyer cancels the report, the chat will reopen automatically.'
                  : closedReason === 'admin_closed'
                    ? (viewerIsAdmin ? '' : 'If you need further help, please wait for admin to reach out again.')
                    : 'You can still read the history. A new order between you and this user will open a fresh chat.'}
              </p>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                className="flex-1 bg-cream-50 border border-cream-100 rounded-2xl px-4 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-[#F4C2C2]/50 focus:border-transparent resize-none max-h-28"
                rows={1}
                data-testid="chat-input"
              />
              <button
                onClick={() => handleSend()}
                disabled={!newMsg.trim() || sending}
                className="bg-[#2C2A29] text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 transition-all hover:bg-[#1a1918] disabled:opacity-30 disabled:hover:bg-[#2C2A29]"
                data-testid="chat-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="chat-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Report Conversation</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">Help us keep Stashmart safe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {['Spam or scam', 'Harassment or abuse', 'Off-platform payment request', 'Fake account', 'Other'].map(r => (
              <button key={r} onClick={() => setReportReason(r)}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-body text-sm transition-all ${
                  reportReason === r
                    ? 'bg-red-50 text-red-600 border border-red-200 font-medium'
                    : 'bg-cream-50 text-[#2C2A29] border border-cream-100 hover:border-red-200'
                }`}
                data-testid={`report-reason-${r.toLowerCase().replace(/[\s/]+/g, '-')}`}>
                {r}
              </button>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setReportDialog(false); setReportReason(''); }}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
              <button onClick={handleReport} disabled={reporting || !reportReason}
                className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="chat-report-submit">
                <Flag size={14} /> {reporting ? 'Submitting...' : 'Report'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
